/**
 * Predicados DOM canónicos sobre la "editabilidad" de un elemento.
 *
 * PATRÓN A SEGUIR: la pregunta "¿este elemento es editable?" se responde SOLO
 * desde aquí. Antes esta lógica (`TAGS.has(tagName) || isContentEditable`)
 * estaba copiada en `lib/gestures/guards`, `lib/interaction/context`,
 * `components/ui/dialog` y `todo-detail.helpers` con cuatro variantes ligeramente
 * distintas, con riesgo de divergir. Importa estos helpers en vez de reescribir
 * el check.
 *
 * Hay dos predicados con semántica DISTINTA — elige según la intención:
 *  - `isEditableElement`: cualquier control de formulario (incluye SELECT,
 *    checkbox…) o `contenteditable`. Úsalo para *suprimir* gestos/atajos
 *    globales cuando el foco está dentro de un control.
 *  - `isTextEntryElement`: SOLO entrada de texto libre (textarea o input de
 *    tipo textual). Más estricto. Úsalo para decisiones basadas en el cursor de
 *    texto, p. ej. no robar las flechas mientras se edita texto.
 *
 * Nota: hay checks puntuales (p. ej. en el manejo de teclas del detalle) que
 * usan conjuntos de tags propios a propósito (excluir SELECT, incluir BUTTON/A…)
 * porque preguntan algo diferente ("¿esta tecla la consume el control?"). Esos
 * NO deben colapsarse aquí: la semántica distinta es intencional.
 */

/** Controles de formulario que cuentan como "editables". */
const FORM_CONTROL_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

/** Tipos de `<input>` que aceptan entrada de texto libre con cursor. */
const TEXT_INPUT_TYPES = new Set(["text", "search", "url", "tel", "email", "password", "number"]);

/**
 * `true` si `el` es un control de formulario (input/textarea/select) o un
 * elemento `contenteditable`.
 */
export function isEditableElement(el: Element | null): el is HTMLElement {
  return el instanceof HTMLElement && (FORM_CONTROL_TAGS.has(el.tagName) || el.isContentEditable);
}

/**
 * `true` si `el` acepta entrada de texto libre: un `<textarea>` o un `<input>`
 * de tipo textual. Más estricto que {@link isEditableElement} (excluye SELECT,
 * checkbox, radio, etc.).
 */
export function isTextEntryElement(
  el: Element | null,
): el is HTMLTextAreaElement | HTMLInputElement {
  if (el instanceof HTMLTextAreaElement) {
    return true;
  }
  if (el instanceof HTMLInputElement) {
    return TEXT_INPUT_TYPES.has(el.type.toLowerCase());
  }
  return false;
}

/**
 * `true` si hay algún campo editable con el foco, o existe cualquier
 * `contenteditable` en el documento. Pensado para gates globales (arrastre de
 * diálogos, swipe de navegación) que deben ceder ante la edición. SSR-safe.
 */
export function isEditingActive(): boolean {
  if (typeof document === "undefined") {
    return false;
  }
  if (document.querySelector('[contenteditable="true"], [contenteditable=""]') !== null) {
    return true;
  }
  return isEditableElement(document.activeElement);
}
